import { inject, Injectable } from '@angular/core';
import { PlayResultService } from './play-result.service';
import { PlayResultDetailsService } from './play-result-details.service';
import { PlayReportingAuditService } from './play-reporting-audit.service';
import { PlayAudit, PlayExecutionReportingData, PlayResult, PlayResultDetail } from '@r1-compass-library/models';
import { forkJoin, from, map, mergeMap, Observable, toArray } from 'rxjs';
import { PlayCompletionStatusService } from './play-completion-status.service';

/**
 * Gets all data play execution reporting data that is relevant to the reporting UI.
 */
@Injectable({
    providedIn: 'root',
})
export class PlayExecutionReportingService {
    readonly #playResultService = inject(PlayResultService);
    readonly #playResultDetailsService = inject(PlayResultDetailsService);
    readonly #playReportingAuditService = inject(PlayReportingAuditService);
    readonly #playCompletionStatusService = inject(PlayCompletionStatusService);

    getExecutionReportingData(
        playId: number,
        startRangeIsoString: string,
        endRangeIsoString: string
    ): Observable<PlayExecutionReportingData[]> {
        return this.#playResultService.listPlayResults(playId, startRangeIsoString, endRangeIsoString).pipe(
            mergeMap((playResults: PlayResult[]) =>
                from(playResults).pipe(
                    mergeMap(
                        (
                            playResult: PlayResult // from each play-result...
                        ) =>
                            forkJoin({
                                playResultDetails: this.#playResultDetailsService.listPlayResultDetails({
                                    playResultId: playResult.id,
                                }), // lookup details...
                                playAudits: this.#playReportingAuditService.getPlayAudits({
                                    playResultId: playResult.id,
                                }), // and lookup associated audit entries...
                            }).pipe(
                                mergeMap(
                                    (
                                        { playResultDetails, playAudits } // pass the details + audits into this function...
                                    ) => this.createPlayResultTableData(playResult, playResultDetails, playAudits) // ...to get the reporting table data for that result
                                )
                            )
                    )
                )
            ),
            toArray() // transform Observable<PlayResultTableData>[] --> Observable<PlayResultTableData[]>
        );
    }

    private createPlayResultTableData(
        playResult: PlayResult,
        playResultDetailsArr: PlayResultDetail[],
        playAudits: PlayAudit[]
    ): Observable<PlayExecutionReportingData> {
        const createDetail = playResultDetailsArr.find(detail => detail.actionType === 'create');
        const updateDetail = playResultDetailsArr.find(detail => detail.actionType === 'update');
        return this.#playCompletionStatusService
            .determineCompletionStatus(playResult, playAudits, createDetail, updateDetail)
            .pipe(
                map(status => ({
                    ...playResult,
                    numRecordsCreated: createDetail?.numRecordsSuccessful ?? 0,
                    numRecordsUpdated: updateDetail?.numRecordsSuccessful ?? 0,
                    numRecordsSelected:
                        (createDetail?.numRecordsSelected ?? 0) + (updateDetail?.numRecordsSelected ?? 0),
                    numRecordsFailed: (createDetail?.numRecordsFailed ?? 0) + (updateDetail?.numRecordsFailed ?? 0),
                    numRecordsRejected:
                        (createDetail?.numRecordsRejected ?? 0) + (updateDetail?.numRecordsRejected ?? 0),
                    status: status,
                }))
            );
    }
}
